import React, { useState, useEffect, useCallback } from 'react';
import { ModelType, IVideoGeneration, PaginationResult, VideoGenerationQuery, PaginationQuery, VideoGenerationStatus, VideoGenerationType, Resolution, AspectRatio } from '@packages/global';
import { videoGenerationApi } from '../services/user';
import { useModelStore } from '../stores/modelStore';
import Pagination from '../components/Pagination';
import VideoGenerationCard from '../components/VideoGeneration/VideoGenerationCard';
import SearchBar from '../components/VideoGeneration/SearchBar';
import VideoGenerationForm from '../components/VideoGeneration/VideoGenerationForm';
import { useTranslation } from 'react-i18next';
import { useDialog } from '../hooks/useDialog';
import { useWebSocketContext } from '../services/websocket/WebSocketContext';
import { MessageType, TaskStatusChangedMessage, TaskType, TaskStatus } from '../services/websocket/types';
import '../styles/line-clamp.css';

interface VideoGenerationFormData {
  prompt: string;
  modelId: string;
  type: VideoGenerationType;
  duration: number;
  width: number;
  height: number;
  aspectRatio: string;
  resolution: Resolution;
  referenceImages: string[];
  referenceAudio?: string;
}

export default function VideoGenerationsPage() {
  const [items, setItems] = useState<IVideoGeneration[]>([]);
  const { models: allModels, loading: modelsLoading, fetchModels, getModelsByType } = useModelStore();
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState<IVideoGeneration | null>(null);
  const { alert, confirm } = useDialog();
  const { t } = useTranslation(['generation', 'common']);
  const { client, isConnected } = useWebSocketContext();
  const [formData, setFormData] = useState<VideoGenerationFormData>({
    prompt: '',
    modelId: '',
    type: VideoGenerationType.TEXT_TO_VIDEO,
    duration: 5,
    width: 1920,
    height: 1080,
    aspectRatio: '16:9',
    resolution: Resolution.HD_720P,
    referenceImages: [],
    referenceAudio: undefined
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<VideoGenerationStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<VideoGenerationType | ''>('');
  const [pagination, setPagination] = useState<PaginationResult>({
    page: 1,
    limit: 24,
    total: 0,
    totalPages: 0,
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });

  const getModelTypeForGenerationType = (type: VideoGenerationType): ModelType => {
    switch (type) {
      case VideoGenerationType.TEXT_TO_VIDEO:
        return ModelType.TEXT_TO_VIDEO;
      case VideoGenerationType.IMAGE_TO_VIDEO:
        return ModelType.IMAGE_TO_VIDEO;
      case VideoGenerationType.KEYFRAME_TO_VIDEO:
        return ModelType.KEYFRAME_TO_VIDEO;
      case VideoGenerationType.MULTI_IMAGE_TO_VIDEO:
        return ModelType.MULTI_IMAGE_TO_VIDEO;
      default:
        return ModelType.TEXT_TO_VIDEO;
    }
  };

  const getFilteredModels = (generationType: VideoGenerationType) => {
    const modelType = getModelTypeForGenerationType(generationType);
    return getModelsByType(modelType);
  };

  const loadItems = async (
    queryParams: Partial<VideoGenerationQuery> = {},
    page: number = 1,
    limit: number = 24
  ) => {
    try {
      const queryData: PaginationQuery<VideoGenerationQuery> = {
        query: {
          search: queryParams.search ?? searchTerm,
          status: queryParams.status ?? (statusFilter || undefined),
          type: queryParams.type ?? (typeFilter || undefined),
          ...queryParams
        },
        pagination: {
          page,
          limit,
          sortBy: 'createdAt',
          sortOrder: 'desc'
        }
      };
      const response = await videoGenerationApi.videoGenerationPageList(queryData);
      setItems(response.items);
      setPagination((prev) => ({
        ...prev,
        page,
        limit,
        total: response.pagination.total,
        totalPages: response.pagination.totalPages
      }));
    } catch (error) {
      console.error('加载视频生成任务失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModels();
    loadItems();
  }, [fetchModels]);

  // 🔥 页面级别订阅 WebSocket，监听所有视频生成任务的状态更新
  useEffect(() => {
    if (!client || !isConnected) {
      console.log('[VideoGenerationsPage] WebSocket not ready, skipping subscription');
      return;
    }

    // 监听状态变化消息
    const unsubscribeStatusChanged = client.onMessage(
      MessageType.TASK_STATUS_CHANGED,
      (message: TaskStatusChangedMessage) => {
        console.log('[VideoGenerationsPage] 📨 Received status change:', {
          taskId: message.taskId,
          taskType: message.taskType,
          newStatus: message.newStatus
        });

        // 只处理视频生成任务
        if (message.taskType !== TaskType.VIDEO_GENERATION) {
          return;
        }

        // 映射 WebSocket 状态到 VideoGenerationStatus
        const statusMap: Record<TaskStatus, VideoGenerationStatus> = {
          [TaskStatus.PENDING]: VideoGenerationStatus.PENDING,
          [TaskStatus.SUBMITTED]: VideoGenerationStatus.SUBMITTED,
          [TaskStatus.PROCESSING]: VideoGenerationStatus.PROCESSING,
          [TaskStatus.COMPLETED]: VideoGenerationStatus.COMPLETED,
          [TaskStatus.FAILED]: VideoGenerationStatus.FAILED
        };

        const newStatus = statusMap[message.newStatus];
        if (!newStatus) {
          console.warn('[VideoGenerationsPage] Unknown status:', message.newStatus);
          return;
        }

        // 更新对应的 item
        setItems(prevItems => {
          const itemIndex = prevItems.findIndex(item => item.id === message.taskId);
          if (itemIndex === -1) {
            console.log('[VideoGenerationsPage] Task not in current list:', message.taskId);
            return prevItems;
          }

          console.log('[VideoGenerationsPage] ✅ Updating task status:', {
            taskId: message.taskId,
            oldStatus: prevItems[itemIndex].status,
            newStatus
          });

          const newItems = [...prevItems];
          newItems[itemIndex] = {
            ...newItems[itemIndex],
            status: newStatus
          };

          // 如果任务完成，获取完整数据（包括视频URL）
          if (newStatus === VideoGenerationStatus.COMPLETED || newStatus === VideoGenerationStatus.FAILED) {
            videoGenerationApi.getVideoGeneration(message.taskId)
              .then(updatedItem => {
                console.log('[VideoGenerationsPage] 📥 Fetched complete task data:', updatedItem.id);
                setItems(prev => prev.map(item =>
                  item.id === updatedItem.id ? updatedItem : item
                ));
              })
              .catch(err => {
                console.error('[VideoGenerationsPage] Failed to fetch task data:', err);
              });
          }

          return newItems;
        });
      }
    );

    // 监听任务完成消息
    const unsubscribeCompleted = client.onMessage(
      MessageType.TASK_COMPLETED,
      (message: any) => {
        if (message.taskType !== TaskType.VIDEO_GENERATION) return;
        console.log('[VideoGenerationsPage] 🎉 Task completed:', message.taskId);
        // 获取完整数据
        videoGenerationApi.getVideoGeneration(message.taskId)
          .then(updatedItem => {
            setItems(prev => prev.map(item =>
              item.id === updatedItem.id ? updatedItem : item
            ));
          })
          .catch(console.error);
      }
    );

    // 监听任务失败消息
    const unsubscribeFailed = client.onMessage(
      MessageType.TASK_FAILED,
      (message: any) => {
        if (message.taskType !== TaskType.VIDEO_GENERATION) return;
        console.log('[VideoGenerationsPage] ❌ Task failed:', message.taskId);
        // 获取完整数据
        videoGenerationApi.getVideoGeneration(message.taskId)
          .then(updatedItem => {
            setItems(prev => prev.map(item =>
              item.id === updatedItem.id ? updatedItem : item
            ));
          })
          .catch(console.error);
      }
    );

    return () => {
      console.log('[VideoGenerationsPage] 🔌 Cleaning up WebSocket subscription');
      unsubscribeStatusChanged();
      unsubscribeCompleted();
      unsubscribeFailed();
    };
  }, [client, isConnected]);

  // 当模型加载完成且表单中没有选择模型时，自动选择第一个模型
  useEffect(() => {
    if (!modelsLoading && !formData.modelId && allModels.length > 0) {
      const currentTypeModels = getFilteredModels(formData.type);
      if (currentTypeModels.length > 0) {
        const firstModel = currentTypeModels[0];
        const supported = firstModel.supportedDurations ?? [];
        const duration = supported.length > 0 ? supported[0] : formData.duration;
        setFormData(prev => ({ ...prev, modelId: firstModel.id, duration }));
      }
    }
  }, [allModels, modelsLoading, formData.modelId, formData.type]);

  const handleSearchTermChange = (term: string) => {
    setSearchTerm(term);
    loadItems({ search: term, status: statusFilter || undefined, type: typeFilter || undefined }, 1);
  };

  const handleStatusFilterChange = (status: VideoGenerationStatus | '') => {
    setStatusFilter(status);
    loadItems({ search: searchTerm, status: status || undefined, type: typeFilter || undefined }, 1);
  };

  const handleTypeFilterChange = (type: VideoGenerationType | '') => {
    setTypeFilter(type);
    loadItems({ search: searchTerm, status: statusFilter || undefined, type: type || undefined }, 1);
  };

  const handleAspectRatioChange = (ratio: string) => {
    setFormData(prev => ({
      ...prev,
      aspectRatio: ratio
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.modelId) {
      await alert(t('video.validation.selectModel'), t('common:warning'), 'warning');
      return;
    }

    // 验证不同模式的必需参数
    switch (formData.type) {
      case VideoGenerationType.TEXT_TO_VIDEO:
        // 文本生成视频只需要prompt
        break;
      case VideoGenerationType.IMAGE_TO_VIDEO:
        if (formData.referenceImages.length !== 1) {
          await alert(t('video.validation.imageToVideo'), t('common:warning'), 'warning');
          return;
        }
        break;
      case VideoGenerationType.KEYFRAME_TO_VIDEO:
        if (formData.referenceImages.length !== 2) {
          await alert(t('video.validation.keyframeToVideo'), t('common:warning'), 'warning');
          return;
        }
        break;
      case VideoGenerationType.MULTI_IMAGE_TO_VIDEO:
        if (formData.referenceImages.length < 2 || formData.referenceImages.length > 4) {
          await alert(t('video.validation.multiImageToVideo'), t('common:warning'), 'warning');
          return;
        }
        break;
      default:
        await alert(t('video.validation.unsupportedType'), t('common:warning'), 'warning');
        return;
    }

    try {
      const newTask = await videoGenerationApi.createVideoGeneration({
        prompt: formData.prompt,
        modelId: formData.modelId,
        type: formData.type,
        duration: formData.duration,
        resolution: formData.resolution,
        aspectRatio: formData.aspectRatio as AspectRatio,
        referenceImages: formData.referenceImages,
        referenceAudio: formData.referenceAudio
      });

      console.log('[VideoGenerationsPage] ✅ Task created:', newTask.id);

      // 🔥 直接把新任务加入列表头部，不需要重新加载整个页面
      setItems(prevItems => [newTask, ...prevItems]);
      setPagination(prev => ({ ...prev, total: prev.total + 1 }));

      handleCloseForm();
    } catch (error) {
      console.error('操作失败:', error);
    }
  };

  const handleEdit = (item: IVideoGeneration) => {
    setEditingItem(item);
    setFormData({
      prompt: item.prompt,
      modelId: item.modelId,
      type: item.type,
      duration: item.duration,
      width: 1920,
      height: 1080,
      aspectRatio: item.aspectRatio,
      resolution: item.resolution,
      referenceImages: item.referenceImages,
      referenceAudio: item.referenceAudio
    });
  };

  const handleDelete = async (id: string) => {
    const confirmed = await confirm(t('video.deleteConfirm'), t('common:confirmDelete'), 'danger');
    if (confirmed) {
      try {
        await videoGenerationApi.deleteVideoGeneration(id);
        loadItems();
      } catch (error) {
        console.error('删除失败:', error);
      }
    }
  };

  const handleCloseForm = () => {
    setEditingItem(null);
    resetForm();
  };

  const resetForm = () => {
    const currentTypeModels = getFilteredModels(VideoGenerationType.TEXT_TO_VIDEO);
    const firstModel = currentTypeModels.length > 0 ? currentTypeModels[0] : null;
    const defaultDuration = firstModel?.supportedDurations?.[0] ?? 5;
    setFormData({
      prompt: '',
      modelId: firstModel?.id ?? '',
      type: VideoGenerationType.TEXT_TO_VIDEO,
      duration: defaultDuration,
      width: 1920,
      height: 1080,
      aspectRatio: '16:9',
      resolution: Resolution.HD_720P,
      referenceImages: [],
      referenceAudio: undefined
    });
  };

  const handleGenerationTypeChange = (type: VideoGenerationType) => {
    const currentTypeModels = getFilteredModels(type);
    const firstModel = currentTypeModels.length > 0 ? currentTypeModels[0] : null;
    const supported = firstModel?.supportedDurations ?? [];
    const duration = supported.length > 0 ? supported[0] : formData.duration;
    setFormData(prev => ({
      ...prev,
      type,
      modelId: firstModel?.id ?? '',
      duration
    }));
  };

  const handleFormDataChange = (data: Partial<VideoGenerationFormData>) => {
    setFormData(prev => {
      const next = { ...prev, ...data };
      // 切换模型时，检查 duration 是否在新模型的 supportedDurations 内
      if (data.modelId) {
        const newModel = getFilteredModels(next.type).find(m => m.id === data.modelId);
        const durations = newModel?.supportedDurations ?? [];
        if (durations.length > 0 && !durations.includes(next.duration)) {
          next.duration = durations[0];
        }
      }
      return next;
    });
  };

  return (
    <div className="flex h-screen bg-bg-page">
      {/* 左侧表单 */}
      <VideoGenerationForm
        editingItem={editingItem}
        formData={formData}
        models={getFilteredModels(formData.type)}
        modelsLoading={modelsLoading}
        onSubmit={handleSubmit}
        onFormDataChange={handleFormDataChange}
        onAspectRatioChange={handleAspectRatioChange}
        onGenerationTypeChange={handleGenerationTypeChange}
        onReset={handleCloseForm}
      />

      {/* 右侧内容区域 */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {/* 页面标题 */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-50">{t('video.title')}</h1>
              <p className="mt-1 text-sm text-gray-400">{t('video.subtitle')}</p>
            </div>
            <SearchBar
              searchTerm={searchTerm}
              statusFilter={statusFilter}
              typeFilter={typeFilter}
              onSearchTermChange={handleSearchTermChange}
              onStatusFilterChange={handleStatusFilterChange}
              onTypeFilterChange={handleTypeFilterChange}
            />
          </div>

          {/* 卡片网格 */}
          {loading ? (
            <div className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm p-12">
              <div className="flex justify-center items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-3 text-gray-400">{t('common:loading')}</span>
              </div>
            </div>
          ) : items.length === 0 ? (
            <div className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm p-12">
              <div className="text-center">
                <p className="text-gray-400">{t('video.empty')}</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,240px)] justify-between gap-x-4 gap-y-6">
              {items.map((item) => (
                <VideoGenerationCard
                  key={item.id}
                  item={item}
                  models={allModels}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}

          {/* 分页组件 */}
          {pagination.totalPages > 1 && (
            <div className="bg-bg-surface rounded-xl border border-border-subtle shadow-sm p-4 mt-6">
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={(page) => loadItems({}, page, pagination.limit)}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}